import { TASK_PROJECT_CHANGED, TASK_PROJECT_ROLE_CHANGED } from '@common/constants/webSockets'
import { ITask, TaskStatus } from '@common/interfaces/task'
import {
  IProjectStatusSummaryData,
  IProjectTasksResponse,
  ITaskProject,
  ITaskProjectChangedEvent,
  ITaskProjectFavorite,
  ITaskProjectTask,
  TaskProjectRole,
} from '@common/interfaces/taskProject'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import { Action, combineReducers } from 'redux'
import { CLIENT_TAB_ID } from '../../helpers/clientTabId'
import { createLoadingDataReducer } from '../../helpers/redux'
import {
  IPlacementMove,
  ISubtaskMove,
  PROJECT_PLACEMENT,
  applySubtaskMove,
  reorderSectionsByIds,
  repositionRows,
} from '../../helpers/taskPlacement'
import { IActionWithParams } from '../apiMiddleware'
import {
  SET_TASKS_CUSTOM_FIELD_VALUE_REQUEST,
  SET_TASK_CUSTOM_FIELD_VALUE_REQUEST,
} from '../customFields/actions'
import {
  BULK_DELETE_TASKS_REQUEST,
  BULK_UPDATE_TASKS_REQUEST,
  CREATE_TASK_REQUEST,
  DELETE_TASK_REQUEST,
  LIST_SUBTASKS_SUCCESS,
  MARK_TASK_READ_REQUEST,
  MERGE_TASKS_REQUEST,
  MOVE_SUBTASK_REQUEST,
  UPDATE_TASK_REQUEST,
} from '../myTasks/actions'
import { ILoadingData } from '../types'
import * as actions from './actions'

// The per-user project toggles answer with the project they just flipped
// (`setProjectFavorite` adds the caller's whole favorites list), so every slice
// holding the flag can be patched in place. Deliberately not wired into any
// reducer's saving list and never followed by a refetch — either one flashes the
// table skeleton or the "Saving…" indicator on what should feel like flipping a
// switch.
type ProjectPatchAction = Action<string> & {
  data?: {
    // SET_PROJECT_FAVORITE_SUCCESS / SET_PROJECT_MY_TASKS_VISIBILITY_SUCCESS
    projectId?: string
    isFavorite?: boolean
    showInMyTasks?: boolean
    favorites?: ITaskProjectFavorite[]
    // UPDATE_PROJECT_SUCCESS returns the saved project
    id?: string
    name?: string
    // TASK_PROJECT_ROLE_CHANGED (plus projectId above)
    targetUserId?: string
    role?: TaskProjectRole
  }
  // DELETE_PROJECT_SUCCESS carries no body, so the id rides on the action
  params?: { id?: string }
}

// TASK_PROJECT_ROLE_CHANGED only reaches the member it is about, so it is the
// viewer's own role: their row's role and their entry in its members list move.
const applyRoleChange = (
  project: ITaskProject,
  { projectId, targetUserId, role }: NonNullable<ProjectPatchAction['data']>,
): ITaskProject =>
  project.id !== projectId || !role
    ? project
    : {
        ...project,
        currentUserRole: role,
        members: project.members?.map((member) =>
          member.userId === targetUserId ? { ...member, role } : member,
        ),
      }

// Sidebar + overview list of the user's projects.
const baseProjects = createLoadingDataReducer<ITaskProject[] | null>(
  [actions.LIST_PROJECTS_REQUEST],
  [actions.CREATE_PROJECT_REQUEST, actions.UPDATE_PROJECT_REQUEST, actions.DELETE_PROJECT_REQUEST],
)

const projects = (state: Parameters<typeof baseProjects>[0], action: ProjectPatchAction) => {
  const next = baseProjects(state, action)
  if (action.type === actions.SET_PROJECT_FAVORITE_SUCCESS && action.data && next.data) {
    const { projectId, isFavorite } = action.data
    return {
      ...next,
      data: next.data.map((project) =>
        project.id === projectId ? { ...project, isFavorite } : project,
      ),
    }
  }
  if (action.type === actions.SET_PROJECT_MY_TASKS_VISIBILITY_SUCCESS && action.data && next.data) {
    const { projectId, showInMyTasks } = action.data
    return {
      ...next,
      data: next.data.map((project) =>
        project.id === projectId ? { ...project, showInMyTasks } : project,
      ),
    }
  }
  if (action.type === TASK_PROJECT_ROLE_CHANGED && action.data && next.data) {
    const change = action.data
    return { ...next, data: next.data.map((project) => applyRoleChange(project, change)) }
  }
  return next
}

// The projects pinned as tabs on the Tasks page, in the order they were
// favorited. Order is server-side and never changes, so the strip stays stable.
const baseFavoriteProjects = createLoadingDataReducer<ITaskProjectFavorite[] | null>([
  actions.LIST_FAVORITE_PROJECTS_REQUEST,
])

const favoriteProjects = (
  state: Parameters<typeof baseFavoriteProjects>[0],
  action: ProjectPatchAction,
) => {
  const next = baseFavoriteProjects(state, action)

  if (action.type === actions.SET_PROJECT_FAVORITE_SUCCESS && action.data?.favorites) {
    return { ...next, isLoading: false, data: action.data.favorites }
  }

  // A deleted project has to lose its tab here rather than on the next reload:
  // the tab stays clickable otherwise, and ShowProject answers ID_NOT_FOUND,
  // which redirects the whole app to "Page not found". The remembered sidebar
  // destination is validated against this list too, so a stale entry sends the
  // user there on their next visit.
  if (action.type === actions.DELETE_PROJECT_SUCCESS && next.data && action.params?.id) {
    const deletedId = action.params.id
    return { ...next, data: next.data.filter(({ id }) => id !== deletedId) }
  }

  // The tab label is the only place a favorite's name is rendered, so a rename
  // that skipped this slice would sit stale until a full page load.
  if (action.type === actions.UPDATE_PROJECT_SUCCESS && next.data && action.data?.id) {
    const { id: renamedId, name } = action.data
    return {
      ...next,
      data: next.data.map((favorite) =>
        favorite.id === renamedId && name ? { ...favorite, name } : favorite,
      ),
    }
  }

  return next
}

// The open project (header, members, sections) on the project page.
const baseSelectedProject = createLoadingDataReducer<ITaskProject | null>(
  [actions.SHOW_PROJECT_REQUEST],
  [
    actions.UPDATE_PROJECT_REQUEST,
    actions.ADD_PROJECT_MEMBER_REQUEST,
    actions.UPDATE_MEMBER_ROLE_REQUEST,
    actions.REMOVE_PROJECT_MEMBER_REQUEST,
    actions.CREATE_SECTION_REQUEST,
    actions.UPDATE_SECTION_REQUEST,
    actions.REORDER_SECTIONS_REQUEST,
    actions.DELETE_SECTION_REQUEST,
  ],
)

const selectedProject = (
  state: Parameters<typeof baseSelectedProject>[0],
  action: ProjectPatchAction,
) => {
  const next = baseSelectedProject(state, action)
  if (
    action.type === actions.SET_PROJECT_FAVORITE_SUCCESS &&
    action.data &&
    next.data?.id === action.data.projectId
  ) {
    return { ...next, data: { ...next.data, isFavorite: action.data.isFavorite } }
  }
  if (action.type === TASK_PROJECT_ROLE_CHANGED && action.data && next.data) {
    return { ...next, data: applyRoleChange(next.data, action.data) }
  }
  return next
}

// Section-grouped task list for the open project. Saving state also tracks the
// shared task mutations (create/update/delete), since inline edits on the
// project page go through the myTasks `updateTask`/`createTask`/`deleteTask`
// actions — this drives the "Saved" indicator like MyTasksPage. Section
// mutations feed it too so add/rename/move/delete section show the same
// indicator (the page renders SaveState from this slice, not selectedProject).
const PAGING_PARAMS = ['page', 'perPage', 'loadMore', 'skipLoader']

// The question a task list answers: its request without the paging params.
const pickTaskListQuery = (params: Record<string, unknown>) => omit(params, PAGING_PARAMS)

const baseProjectTasks = createLoadingDataReducer<IProjectTasksResponse | null>(
  [actions.LIST_PROJECT_TASKS_REQUEST],
  [
    actions.ADD_TASK_TO_PROJECT_REQUEST,
    actions.ADD_TASKS_TO_PROJECT_REQUEST,
    actions.MOVE_TASK_PLACEMENT_REQUEST,
    actions.REMOVE_TASK_FROM_PROJECT_REQUEST,
    actions.CREATE_SECTION_REQUEST,
    actions.UPDATE_SECTION_REQUEST,
    actions.REORDER_SECTIONS_REQUEST,
    actions.DELETE_SECTION_REQUEST,
    CREATE_TASK_REQUEST,
    UPDATE_TASK_REQUEST,
    DELETE_TASK_REQUEST,
    SET_TASK_CUSTOM_FIELD_VALUE_REQUEST,
    SET_TASKS_CUSTOM_FIELD_VALUE_REQUEST,
    BULK_UPDATE_TASKS_REQUEST,
    BULK_DELETE_TASKS_REQUEST,
    MERGE_TASKS_REQUEST,
  ],
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  { pickQuery: (params) => pickTaskListQuery(params) },
)

// Lazily-loaded subtasks come back via the shared myTasks `listSubtasks` action,
// so fold them into the matching task within its section (mirrors the My Tasks
// reducer) — otherwise expanded rows would never show their subtasks here.
type SubtasksAction = Action<string> & {
  params?: { parentTaskId?: string; loadMore?: boolean }
  data?: { data?: ITask[] }
}

type IMoveTaskPlacementParams = IPlacementMove & {
  projectId: string
  isManualOrder: boolean
  preview?: IPlacementMove[]
}

const byPlacementPosition = (a: ITaskProjectTask, b: ITaskProjectTask): number =>
  a.placement.position - b.placement.position

// In manual order a section re-sorts by position, as the refetch will return it. Under a
// column sort the refetch keeps the sort, so rows keep their order and only the section changes.
const applyProjectTaskMove = (
  data: IProjectTasksResponse,
  move: IMoveTaskPlacementParams,
): IProjectTasksResponse => {
  const tasks = data.sections.flatMap((section) => section.tasks)
  const moved = tasks.find((task) => task.id === move.taskId)
  // A section created a moment ago (the panel's create-and-move) isn't loaded yet; the
  // refetch places the row rather than filing it somewhere nothing renders.
  const isTargetLoaded = data.sections.some((section) => section.id === move.sectionId)
  if (!moved || moved.placement.projectId !== move.projectId || !isTargetLoaded) {
    return data
  }
  const repositioned = new Map(
    repositionRows(tasks, move, PROJECT_PLACEMENT).map((task) => [task.id, task]),
  )
  const ordered = move.isManualOrder
    ? [...repositioned.values()].sort(byPlacementPosition)
    : tasks.map((task) => repositioned.get(task.id) ?? task)
  const fromSectionId = moved.placement.sectionId
  const sections = data.sections.map((section) => ({
    ...section,
    tasks: ordered.filter((task) => task.placement.sectionId === section.id),
    matchingTaskCount:
      section.matchingTaskCount +
      Number(section.id === move.sectionId) -
      Number(section.id === fromSectionId),
  }))
  return { ...data, sections }
}

// Rows back into their sections, in array order.
const regroupProjectTasks = (
  data: IProjectTasksResponse,
  rows: ITaskProjectTask[],
): IProjectTasksResponse => ({
  ...data,
  sections: data.sections.map((section) => {
    const tasks = rows.filter((task) => task.placement.sectionId === section.id)
    return {
      ...section,
      tasks,
      matchingTaskCount: section.matchingTaskCount + tasks.length - section.tasks.length,
    }
  }),
})

// Clears the dot on the loaded rows `isRead` picks, taking them off the view's unread count.
const clearUnreadBoardRows = (
  data: IProjectTasksResponse,
  isRead: (task: ITaskProjectTask) => boolean,
): IProjectTasksResponse => {
  let clearedCount = 0
  const sections = data.sections.map((section) => ({
    ...section,
    tasks: section.tasks.map((task) => {
      if (!task.isUnread || !isRead(task)) {
        return task
      }
      clearedCount += 1
      return { ...task, isUnread: false }
    }),
  }))
  if (clearedCount === 0) {
    return data
  }
  const unread = Math.max(0, (data.counts?.unread ?? 0) - clearedCount)
  return { ...data, sections, counts: { ...data.counts, unread } }
}

// A promoted task becomes a row in the board section it was dropped on, ahead of the row
// that held its slot. A promote aimed at another board (or none — My Tasks) is left to the
// refetch: the server keeps the task on this board, below its parent.
const applyProjectSubtaskMove = (
  data: IProjectTasksResponse,
  move: ISubtaskMove,
  projectId: string | undefined,
): IProjectTasksResponse => {
  if (move.parentTaskId === null && projectId !== data.projectId) {
    return data
  }
  const tasks = data.sections.flatMap((section) => section.tasks)
  const { rows, detached } = applySubtaskMove(tasks, move)
  if (rows === tasks) {
    return data
  }
  const { sectionId, position } = move
  if (!detached || !data.sections.some((section) => section.id === sectionId)) {
    return regroupProjectTasks(data, rows)
  }
  const row: ITaskProjectTask = {
    ...detached,
    placement: {
      id: '',
      projectId: data.projectId,
      sectionId,
      position: Number.MAX_SAFE_INTEGER,
    },
  }
  const slot = rows.findIndex(
    (task) =>
      task.placement.sectionId === sectionId &&
      position !== undefined &&
      task.placement.position >= position,
  )
  const withRow = slot === -1 ? [...rows, row] : [...rows.slice(0, slot), row, ...rows.slice(slot)]
  const placed = new Map(
    repositionRows(
      withRow,
      { taskId: row.id, sectionId: row.placement.sectionId, position },
      PROJECT_PLACEMENT,
    ).map((task) => [task.id, task]),
  )
  return regroupProjectTasks(
    data,
    withRow.map((task) => placed.get(task.id) ?? task),
  )
}

// A load-more page carries only its own rows plus every section header: add its rows to the
// end of their sections. The base reducer would replace instead.
const appendTaskPage = (
  current: IProjectTasksResponse,
  incoming: IProjectTasksResponse,
): IProjectTasksResponse => {
  const incomingById = new Map(incoming.sections.map((section) => [section.id, section]))
  const mergedSections = current.sections.map((prevSection) => {
    const next = incomingById.get(prevSection.id)
    return next ? { ...next, tasks: [...prevSection.tasks, ...next.tasks] } : prevSection
  })
  incoming.sections.forEach((section) => {
    if (!mergedSections.some((existing) => existing.id === section.id)) {
      mergedSections.push(section)
    }
  })
  return { ...incoming, sections: mergedSections }
}

// A load-more page lands only on the list it was asked for: once a reload with another
// filter, sort or project has started, the page answers a stale question and is dropped.
const appendPageIfCurrent = (
  state: ILoadingData<IProjectTasksResponse | null>,
  params: Record<string, unknown>,
  incoming: IProjectTasksResponse,
): ILoadingData<IProjectTasksResponse | null> =>
  state.data && isEqual(pickTaskListQuery(params), state.query)
    ? { ...state, data: appendTaskPage(state.data, incoming) }
    : state

const projectTasks = (state: Parameters<typeof baseProjectTasks>[0], action: SubtasksAction) => {
  if (
    action.type === actions.LIST_PROJECT_TASKS_SUCCESS &&
    action.params?.loadMore &&
    state?.data
  ) {
    const incoming = (action as Action<string> & { data?: IProjectTasksResponse }).data
    if (incoming) {
      return appendPageIfCurrent(state, action.params ?? {}, incoming)
    }
  }

  const next = baseProjectTasks(state, action)
  // Optimistic: the row lands where it was dropped before the write returns. Claiming the
  // latest request id drops any list response already in flight, so it can't undo the move.
  if (action.type === actions.MOVE_TASK_PLACEMENT_REQUEST && next.data) {
    const { params, requestId } = action as IActionWithParams<IMoveTaskPlacementParams>
    const data = params
      ? (params.preview ?? [params]).reduce(
          (current, move) => applyProjectTaskMove(current, { ...params, ...move }),
          next.data,
        )
      : next.data
    if (data !== next.data) {
      return { ...next, latestRequestId: requestId, data }
    }
  }
  if (action.type === MOVE_SUBTASK_REQUEST && next.data) {
    const { params, requestId } = action as IActionWithParams<
      ISubtaskMove & { projectId?: string; preview?: ISubtaskMove[] }
    >
    const data = params
      ? (params.preview ?? [params]).reduce(
          (current, move) => applyProjectSubtaskMove(current, move, params.projectId),
          next.data,
        )
      : next.data
    if (data !== next.data) {
      return { ...next, latestRequestId: requestId, data }
    }
  }
  if (action.type === actions.REORDER_SECTIONS_REQUEST && next.data) {
    const { params, requestId } = action as IActionWithParams<{
      projectId: string
      sectionIds: string[]
    }>
    if (params?.projectId === next.data.projectId) {
      const sections = reorderSectionsByIds(next.data.sections, params.sectionIds)
      return { ...next, latestRequestId: requestId, data: { ...next.data, sections } }
    }
  }
  // Board rows carry the same unread dot as My Tasks, so opening a task clears it here too.
  if (action.type === MARK_TASK_READ_REQUEST && next.data) {
    const taskId = (action as IActionWithParams<{ taskId?: string }>).params?.taskId
    const data = clearUnreadBoardRows(next.data, (task) => task.id === taskId)
    if (data !== next.data) {
      return { ...next, data }
    }
  }
  // Optimistic, like a single read. The server clears every page of the view,
  // so the view's count goes to zero, not just down by the loaded rows.
  if (action.type === actions.MARK_ALL_PROJECT_TASKS_READ_REQUEST && next.data) {
    const projectId = (action as IActionWithParams<{ projectId?: string }>).params?.projectId
    if (projectId === next.data.projectId) {
      const data = clearUnreadBoardRows(next.data, () => true)
      return { ...next, data: { ...data, counts: { ...data.counts, unread: 0 } } }
    }
  }
  if (action.type === LIST_SUBTASKS_SUCCESS) {
    const parentId = action.params?.parentTaskId
    if (next.data && parentId) {
      const rows = action.data?.data ?? []
      return {
        ...next,
        data: {
          ...next.data,
          sections: next.data.sections.map((section) => ({
            ...section,
            tasks: section.tasks.map((task) =>
              task.id === parentId
                ? {
                    ...task,
                    subtasks: action.params?.loadMore ? [...(task.subtasks ?? []), ...rows] : rows,
                  }
                : task,
            ),
          })),
        },
      }
    }
  }
  return next
}

// The latest change someone else made to a project this tab has open (or a
// resync after a dropped socket). `revision` is what the page reacts to; the
// rest says what, beyond the task list, needs refreshing.
export interface ILiveProjectChange extends Omit<ITaskProjectChangedEvent, 'originTabId'> {
  revision: number
  isResync: boolean
}

type LiveProjectChangeAction = Action<string> & {
  data?: ITaskProjectChangedEvent
  projectId?: string
}

const liveProjectChange = (
  state: ILiveProjectChange | null = null,
  action: LiveProjectChangeAction,
): ILiveProjectChange | null => {
  const revision = (state?.revision ?? 0) + 1
  if (action.type === TASK_PROJECT_CHANGED && action.data) {
    const { originTabId, ...change } = action.data
    // This tab's own write: it already refetched when the request returned.
    if (originTabId && originTabId === CLIENT_TAB_ID) {
      return state
    }
    return { ...change, revision, isResync: false }
  }
  if (action.type === actions.PROJECT_UPDATES_RESYNC && action.projectId) {
    return {
      projectId: action.projectId,
      taskIds: [],
      deletedTaskIds: [],
      hasSectionChanges: true,
      hasProjectChanges: true,
      isProjectDeleted: false,
      removedUserIds: [],
      revision,
      isResync: true,
    }
  }
  return state
}

// Drops the task from wherever it sits, then files it back under its own section if `keep`.
const placeCalendarTask = (
  data: IProjectTasksResponse,
  task: ITaskProjectTask,
  keep: boolean,
): IProjectTasksResponse => ({
  ...data,
  sections: data.sections.map((section) => ({
    ...section,
    tasks: [
      ...section.tasks.filter((row) => row.id !== task.id),
      ...(keep && section.id === task.placement.sectionId ? [task] : []),
    ],
  })),
})

const setCalendarTaskStatus = (
  data: IProjectTasksResponse,
  taskId: string,
  status: TaskStatus,
): IProjectTasksResponse => ({
  ...data,
  sections: data.sections.map((section) => ({
    ...section,
    tasks: section.tasks.map((task) => (task.id === taskId ? { ...task, status } : task)),
  })),
})

type UpdateTaskRequestAction = IActionWithParams<{ id: string; data: { status?: TaskStatus } }>

// A day move or a status toggle shows at once; the caller refetches afterwards, which
// also undoes a failed one.
const calendarTasksReducer = (
  [listRequest, listSuccess]: string[],
  isDatedSlice: boolean,
): ((
  state: ILoadingData<IProjectTasksResponse | null> | undefined,
  action: Action<string>,
) => ILoadingData<IProjectTasksResponse | null>) => {
  const base = createLoadingDataReducer<IProjectTasksResponse | null>(
    [listRequest],
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    { pickQuery: (params) => pickTaskListQuery(params) },
  )
  return (state, action) => {
    const page = action as IActionWithParams<Record<string, unknown>, IProjectTasksResponse>
    if (action.type === listSuccess && page.params?.loadMore && state?.data && page.data) {
      return appendPageIfCurrent(state, page.params, page.data)
    }
    const next = base(state, action)
    if (!next.data) {
      return next
    }
    if (action.type === UPDATE_TASK_REQUEST) {
      const { id, data } = (action as UpdateTaskRequestAction).params ?? {}
      return id && data?.status
        ? { ...next, data: setCalendarTaskStatus(next.data, id, data.status) }
        : next
    }
    if (action.type !== actions.MOVE_CALENDAR_TASK) {
      return next
    }
    const { task, dueDate } = action as actions.MoveCalendarTaskAction
    return {
      ...next,
      data: placeCalendarTask(next.data, { ...task, dueDate }, (dueDate !== null) === isDatedSlice),
    }
  }
}

const projectCalendarTasks = calendarTasksReducer(
  [actions.LIST_PROJECT_CALENDAR_TASKS_REQUEST, actions.LIST_PROJECT_CALENDAR_TASKS_SUCCESS],
  true,
)
const projectUndatedTasks = calendarTasksReducer(
  [actions.LIST_PROJECT_UNDATED_TASKS_REQUEST, actions.LIST_PROJECT_UNDATED_TASKS_SUCCESS],
  false,
)

// Arc's shared status summary of the open project. A refresh is a saving action so the
// current summary stays up while Arc works; its reply then replaces it, unless the slot
// has since moved on to another project.
const projectStatusSummary = createLoadingDataReducer<IProjectStatusSummaryData | null>(
  [actions.GET_PROJECT_STATUS_SUMMARY_REQUEST],
  [actions.REFRESH_PROJECT_STATUS_SUMMARY_REQUEST, actions.SAVE_PROJECT_STATUS_UPDATE_REQUEST],
  true,
  undefined,
  undefined,
  (state, action) => {
    const data = (action as Action & { data: IProjectStatusSummaryData }).data
    return !state.data || state.data.projectId === data.projectId ? { ...state, data } : state
  },
)

export default combineReducers({
  projects,
  favoriteProjects,
  selectedProject,
  projectTasks,
  projectCalendarTasks,
  projectUndatedTasks,
  projectStatusSummary,
  liveProjectChange,
})
